/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1671667200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Thu Dec 22 2022 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "dynamodb",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Getting started",
          "depth": 0,
          "id": "getting-started"
        }, {
          "value": "Authenticating with a cross-account role",
          "depth": 1,
          "id": "authenticating-with-a-cross-account-role"
        }, {
          "value": "Authenticating with an access key",
          "depth": 1,
          "id": "authenticating-with-an-access-key"
        }, {
          "value": "Syncing data",
          "depth": 0,
          "id": "syncing-data"
        }, {
          "value": "Table selection",
          "depth": 1,
          "id": "table-selection"
        }, {
          "value": "Record matching",
          "depth": 1,
          "id": "record-matching"
        }, {
          "value": "Field mapping",
          "depth": 1,
          "id": "field-mapping"
        }, {
          "value": "Testing a sync",
          "depth": 1,
          "id": "testing-a-sync"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Rate limits",
          "depth": 1,
          "id": "rate-limits"
        }, {
          "value": "Empty field mappings",
          "depth": 1,
          "id": "empty-field-mappings"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Using the Hightouch debugger",
          "depth": 1,
          "id": "using-the-hightouch-debugger"
        }, {
          "value": "Setting up sync alerts",
          "depth": 1,
          "id": "setting-up-sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      pre: "pre",
      code: "code",
      span: "span",
      h3: "h3",
      h4: "h4"
    }, _provideComponents(), props.components), {NoCommonErrors, LiveDebugger} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "This destination combines the analytical power of your data warehouse to power your high-performance applications at any scale."
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Items"
              })
            }), _jsxs(_components.td, {
              children: ["Sync rows from any source as ", _jsx(_components.a, {
                href: "https://docs.aws.amazon.com/amazondynamodb/latest/developerguide/WorkingWithItems.html",
                children: "items"
              }), " into DynamoDB"]
            }), _jsx(_components.td, {
              children: "Upsert"
            })]
          })
        })]
      }), "\n", _jsx(_components.h2, {
        id: "getting-started",
        children: "Getting started"
      }), "\n", _jsx(_components.p, {
        children: "To connect to your DynamoDB instance, you need to provide Hightouch access to your AWS account. You can do this via access key or cross-account role.\nFor either method, you must give permission for the following actions:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-arduino",
          children: ["    ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"dynamodb:BatchGetItem\""
          }), ",\n    ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"dynamodb:BatchWriteItem\""
          }), ",\n    ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"dynamodb:PutItem\""
          }), ",\n    ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"dynamodb:DescribeTable\""
          }), ",\n    ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"dynamodb:DeleteItem\""
          }), ",\n    ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"dynamodb:UpdateItem\""
          }), ",\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "authenticating-with-a-cross-account-role",
        children: "Authenticating with a cross-account role"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch recommends using a cross-account role for cleaner access. See our ", _jsx(_components.a, {
          href: "/security/aws#cross-account-role",
          children: "AWS security page"
        }), " to learn more about how to use the cross-account role."]
      }), "\n", _jsx(_components.h3, {
        id: "authenticating-with-an-access-key",
        children: "Authenticating with an access key"
      }), "\n", _jsxs(_components.p, {
        children: ["You can give Hightouch access to your AWS account with an access key. Copy your Access key ID and Secret access key into Hightouch. See the ", _jsx(_components.a, {
          href: "https://docs.aws.amazon.com/general/latest/gr/aws-access-keys-best-practices.html",
          children: "AWS docs"
        }), " for best practices."]
      }), "\n", _jsx(_components.h2, {
        id: "syncing-data",
        children: "Syncing data"
      }), "\n", _jsx(_components.p, {
        children: "Once you've authenticated DynamoDB in Hightouch, you need to set up your sync configuration."
      }), "\n", _jsx(_components.h3, {
        id: "table-selection",
        children: "Table selection"
      }), "\n", _jsx(_components.p, {
        children: "In the sync configuration page, select a DynamoDB table to sync to. If your table doesn't exist yet, create it in AWS."
      }), "\n", _jsx(_components.h3, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["match rows in your model with records in DynamoDB via DynamoDB's primary key. See AWS's guide on ", _jsx(_components.a, {
          href: "https://aws.amazon.com/blogs/database/choosing-the-right-dynamodb-partition-key/",
          children: "how to choose a primary key"
        }), " to learn more."]
      }), "\n", _jsx(_components.h3, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch infers field types from the fields values of rows."
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Hightouch sends the ", _jsx(_components.code, {
            children: "timestamp"
          }), " field to DynamoDB as an ISO string. If you\nwant to format it differently, format the value in your model and set the type\nto ", _jsx(_components.code, {
            children: "String"
          }), "."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "delete-behavior",
        children: "Delete behavior"
      }), "\n", _jsxs(_components.p, {
        children: ["When rows are deleted from your source, you can ", _jsx(_components.code, {
          children: "do nothing"
        }), ", ", _jsx(_components.code, {
          children: "clear"
        }), ", or ", _jsx(_components.code, {
          children: "delete"
        }), " them in DynamoDB."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Do nothing"
              })
            }), _jsx(_components.td, {
              children: "Keep the synced record in your DynamoDB"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Clear"
              })
            }), _jsx(_components.td, {
              children: "Remove the synced fields of the synced record from your DynamoDB"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Delete"
              })
            }), _jsx(_components.td, {
              children: "Delete the synced record from your DynamoDB"
            })]
          })]
        })]
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["Deletions are destructive and irreversible. Be sure this is the behavior you\nwant when selecting ", _jsx(_components.code, {
            children: "clear"
          }), " or ", _jsx(_components.code, {
            children: "delete"
          }), "."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "testing-a-sync",
        children: "Testing a sync"
      }), "\n", _jsx(_components.p, {
        children: "Before running your sync, Hightouch recommends that you test your sync configuration by syncing a single row. You can verify that a row was successfully synced by going to your DynamoDB instance table and finding the synced row by its primary key."
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "rate-limits",
        children: "Rate limits"
      }), "\n", _jsxs(_components.p, {
        children: ["DynamoDB has quotas that may limit your syncs. If that happens, Hightouch ", _jsx(_components.a, {
          href: "https://hightouch.com/docs/syncs/retries",
          children: "retries failed rows"
        }), ". For more information, refer to DynamoDB's ", _jsx(_components.a, {
          href: "https://docs.aws.amazon.com/amazondynamodb/latest/developerguide/ServiceQuotas.html#default-limits-throughput-capacity-modes",
          children: "throughput quotas"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "empty-field-mappings",
        children: "Empty field mappings"
      }), "\n", _jsx(_components.p, {
        children: "DynamoDB is a key/value NoSQL database. That means the database doesn't store metadata on its columns and column names and you won't see any suggested fields during field mapping in Hightouch."
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "using-the-hightouch-debugger",
        children: "Using the Hightouch debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "setting-up-sync-alerts",
        children: "Setting up sync alerts"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch can alert you of sync issues via Slack, PagerDuty, SMS, or email. For details, please visit our ", _jsx(_components.a, {
          href: "/syncs/alerting",
          children: "article on alerting"
        }), "."]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
